import React, { useEffect } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ id, type, message, duration = 5000, onClose }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-white border-green-500',
          icon: 'text-green-600',
          iconElement: <CheckCircle2 className="w-5 h-5" />,
          text: 'text-gray-900'
        };
      case 'error':
        return {
          container: 'bg-white border-red-500',
          icon: 'text-red-600',
          iconElement: <AlertTriangle className="w-5 h-5" />,
          text: 'text-gray-900'
        };
      case 'warning':
        return {
          container: 'bg-white border-yellow-500',
          icon: 'text-yellow-600',
          iconElement: <AlertTriangle className="w-5 h-5" />,
          text: 'text-gray-900'
        };
      case 'info':
      default:
        return {
          container: 'bg-white border-blue-500',
          icon: 'text-blue-600',
          iconElement: <Info className="w-5 h-5" />,
          text: 'text-gray-900'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className={`
        flex items-center gap-3 min-w-[300px] max-w-[500px] p-4 rounded-lg border-l-4 shadow-lg
        ${styles.container}
        animate-in slide-in-from-right-full duration-300
      `}
      role="alert"
    >
      <div className={styles.icon}>
        {styles.iconElement}
      </div>
      <p className={`flex-1 text-sm font-medium ${styles.text}`}>
        {message}
      </p>
      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-700 transition-colors"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Toast;
